import { TransferType, resolvePersonName } from '@repo/types'
import type { Customer, Company, User } from 'types'

/**
 * WHO A PERSON IS, on a surface someone OTHER than that person can read.
 *
 * `resolveUserIdentity` (`@repo/types`, and the copy in `components/UI/UserPreview`)
 * ends `name -> @handle -> EMAIL -> '—'`. Its own docblock defends the email step on
 * two conditions: the viewer typed that address themselves, and it is their own screen.
 * NEITHER holds on a certificate page. A certificate is shareable — the whole product
 * is a link you hand to someone — so the reader of a provenance row is, by design,
 * frequently not the person in it. Rendering a real address there discloses PII to an
 * arbitrary viewer and, on the public route, to an anonymous one.
 *
 * So this resolver stops where `resolvePersonName` stops: a real name, else the chosen
 * `@handle`, else NOTHING. It returns `null` rather than a placeholder, because the
 * caller — not this function — owns what an unnameable person looks like on its layout.
 *
 * ⚠️ THE HANDLE IS NOT ALWAYS THERE. `users.username` is nullable, and the shape this
 * receives from the provenance endpoint is an `owners` row widened with its linked
 * user's `username` (`transfer.drizzle.repository.ts` maps `toCustomer_username`);
 * `owners` itself has no such column. Every owner in the dev database happens to carry
 * one, so the both-absent branch is REACHABLE BUT UNEXERCISED by any local fixture.
 *
 * Lives here rather than beside `resolvePersonName` in `@repo/types` only because that
 * package is consumed by `apps/api` from a built `dist/`, which `apps/app` bypasses via
 * a Vite source alias — adding an export there means a rebuild that a concurrently
 * running API session would be paying for. Move it when that is not the case (§36/§47).
 */
export const resolvePublicIdentity = (
  person?: {
    first_name?: string | null
    last_name?: string | null
    username?: string | null
  } | null
): { name: string | null; subtext: string | null } => {
  const name = resolvePersonName(person)
  if (!name) return { name: null, subtext: null }

  const handle = person?.username?.trim() ? `@${person.username.trim()}` : null
  // A resolved value EQUAL to the handle means no real name was found and the handle is
  // itself the primary line. Repeating it underneath prints the same string twice.
  return { name, subtext: name === handle ? null : handle }
}

export const getName = (customer: Customer | User) => {
  if (!customer) return ''
  return customer.first_name && customer.last_name
    ? customer.first_name + ' ' + customer.last_name
    : customer.first_name
    ? customer.first_name
    : customer.last_name
    ? customer.last_name
    : ''
}

/**
 * VERA-304 — who the certificate's CURRENT owner is, for the "Current owner" card.
 *
 * ## `pendingFrom`, and the empty card it fixes
 *
 * While a gift is pending, ownership has NOT moved — the sender still owns the item
 * until the recipient claims. But the recipient's payload for
 * `GET /certificates/public/:id` carries `customer: null` AND no `company` key at all
 * (measured against certificate `dd617005` on 2026-08-30), so this resolved to
 * `{ title: undefined }` and `OwnerProvenanceCard` rendered its `name || '—'` fallback.
 * The card said nobody owned the item, on the one screen where the question "whose is
 * this right now?" is live.
 *
 * The sender IS in that payload, as `transfer.fromCustomer`. So callers pass it here and
 * it is used ONLY when there is no `customer` to name — never in preference to one.
 *
 * This is not a leak: the API withholds `transfer` entirely from an anonymous caller
 * (verified — an unauthenticated fetch of the same certificate returns
 * `customer: null, transfer: null`), so `pendingFrom` is non-null only where the server
 * has already decided this viewer may see the counterparty.
 *
 * ## Why the label goes through `resolvePublicIdentity`
 *
 * The person branch used to test `first_name || last_name` and otherwise return `title:
 * ''` with the raw `email` as the sub-line. Both halves were wrong for the common case:
 * a gift counterparty frequently has NO name (that is the point of gifting to someone
 * who may not have an account), so the card showed `—` above their email address.
 *
 * It then went through `resolveUserIdentity`, which fixed the sub-line but moved the
 * same address UP: that resolver's last resort is the email itself, so an owner with no
 * name and no handle got their address rendered as their NAME. `resolvePublicIdentity`
 * is the same ordering minus that step — see its docblock for why a certificate page is
 * exactly the surface the email fallback was never defensible on.
 */
export const getOwner = (
  customer: Customer | null,
  company: Company,
  tranferType: TransferType | null,
  pendingFrom?: Customer | null,
) => {
  const person = customer ?? (tranferType === TransferType.Primary ? null : pendingFrom)

  if (!person || tranferType === TransferType.Primary) {
    return {
      title: company?.name,
      // Present-but-undefined on purpose. Both branches of this return have to carry
      // the same keys, or `ownerData?.subtext` at a call site is a property access on
      // a union member that lacks it — a type error, not a silent `undefined`.
      subtext: undefined as string | undefined,
      image: company?.logo,
      type: 'company' as const,
    }
  }

  const { name, subtext } = resolvePublicIdentity(person)

  return {
    // `null` when nobody can be named. Left as-is rather than substituted here: the
    // card owns its own empty state, and a placeholder invented at this call site
    // would give one screen two different ways of saying the same nothing.
    title: name ?? undefined,
    // The `@handle`, or nothing — deliberately NEVER `person.email` (see above).
    subtext: subtext ?? undefined,
    image: person.avatar || '',
    type: 'customer' as const,
  }
}

export const getContrastColor = (color: string) => {
  const rgb = parseInt(color.slice(1), 16)
  const r = (rgb >> 16) & 0xff
  const g = (rgb >> 8) & 0xff
  const b = (rgb >> 0) & 0xff
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b

  return luma < 128 ? '#fff' : '#000'
}

export const isLightColor = (color: string) => {
  const rgb = parseInt(color.slice(1), 16)
  const r = (rgb >> 16) & 0xff
  const g = (rgb >> 8) & 0xff
  const b = (rgb >> 0) & 0xff
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luma > 128 ? true : false
}

export const changeColorContrast = (hexColor: string, percentage: number) => {
  const bigint = parseInt(hexColor.slice(1), 16)
  let red = (bigint >> 16) & 255
  let green = (bigint >> 8) & 255
  let blue = bigint & 255

  const brightness = (red * 299 + green * 587 + blue * 114) / 1000

  const isLightColor = brightness > 128

  if (isLightColor) {
    red -= red * (percentage / 100)
    green -= green * (percentage / 100)
    blue -= blue * (percentage / 100)
  } else {
    red += (255 - red) * (percentage / 100)
    green += (255 - green) * (percentage / 100)
    blue += (255 - blue) * (percentage / 100)
  }

  const newHexColor =
    '#' +
    Math.round(red).toString(16).padStart(2, '0') +
    Math.round(green).toString(16).padStart(2, '0') +
    Math.round(blue).toString(16).padStart(2, '0')

  return newHexColor
}

export const getNameOrEmailSlug = (
  item:
    | {
        first_name?: string | null
        last_name?: string | null
        username?: string | null
        email?: string | null
      }
    | null
    | undefined
) => {
  if (!item) return null

  const first = item.first_name?.trim()
  const last = item.last_name?.trim()
  if (first || last) return [first, last].filter(Boolean).join(' ')

  // VERA-304: `username` now sits between the real name and the email slug. A gift
  // recipient very often has NO name at all — the whole point of a gift is sending to
  // someone who may not have finished (or ever started) an account — so this branch is
  // the common path here, not an edge case. A chosen `@handle` is both a better label
  // than a machine-derived email local-part and less of a leak: the transfer rows this
  // feeds are shown to the OTHER party, and `UserPreview`'s own contract (from this
  // epic's verify walkthrough) is that email/phone are never rendered in them.
  const username = item.username?.trim()
  if (username) return `@${username}`

  // VERA-476: `email` was assumed non-null and called `.split('@')` directly. The API can
  // return a user object whose fields are ALL null (see the action-log owner join), so a
  // truthy item with no name and no email crashed here rather than degrading to
  // "Anonymous". Returning null lets callers apply their own fallback copy.
  // Kept as the LAST resort, and deliberately the local-part only — never the full address.
  const email = item.email?.trim()
  if (email) return email.split('@')[0]

  return null
}

export const isEmailValid = (email: string) => {
  return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) !== null;
};


export const base64EncodeEmail = (email: string) => {
  return Buffer.from(email).toString("base64");
};

export const base64DecodeEmail = (email: string) => {
  return Buffer.from(email, "base64").toString("utf8");
};

export const isEmpty = (value: any) => {
  return (
    value === "" ||
    value === null ||
    value === undefined ||
    value === "null" ||
    value === "undefined" ||
    !value
  );
};